import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CHeckGreenIcon from "../../assets/svg/check-green.svg";
import InfoIcon from "../../assets/svg/info.svg";
import ChooseAddOn from "./ChooseAddOn";
import ChooseQuantity from "./ChooseQuatity";
import GrindingTexture from "./GrindingTexture";
import Cart from "./Cart";
import Payment from "./Payment";
import { AddToCart, UpdateCartItem } from "../../Services/CartService";

const steps = [
    'Choose Add-on',
    'Select Quantity',
    'Grinding & Packaging',
    'Cart Detail',
    'Payment'
];

const getStepContent = (
    step,
    onSelectionChange,
    selectedProducts,
    handleGrindingSelectionChange,
    onWheatSizeChange,
    selectedGrinding,
    selectedPackaging,
    productQuantities,
    onProductQuantitiesChange,
    wheatProduct,
    setProductsLoaded,
    isGoingBack,
    isCustomisePayment,
    setIsCustomisePayment,
    cartData,
    setCartData
) => {
    switch (step) {
        case 0:
            return (
                <ChooseAddOn
                    onSelectionChange={onSelectionChange}
                    selectedProducts={selectedProducts}
                    onProductsLoaded={() => setProductsLoaded(true)}
                    isGoingBack={isGoingBack}
                />
            );
        case 1:
            return (
                <ChooseQuantity
                    autoproductQuantities={productQuantities}
                    selectedProducts={selectedProducts}
                    onWheatSizeChange={onWheatSizeChange}
                    onProductQuantitiesChange={onProductQuantitiesChange}
                />
            );
        case 2:
            return (
                <GrindingTexture
                    onSelectionChange={handleGrindingSelectionChange}
                    selectedProducts={selectedProducts}
                />
            );
        case 3:
            return <Cart setCartData={setCartData} />;
        case 4:
            return (
                <Payment
                    isCustomisePayment={isCustomisePayment}
                    setIsCustomisePayment={setIsCustomisePayment}
                    cartData={cartData}
                />
            );
        default:
            return <p>Invalid Step</p>;
    }
};

const CustomiseSidebar = () => {
    const location = useLocation();
    const { cartItems } = location.state || {};
    const cartItemToUse = cartItems || null;

    const [step, setStep] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalWeight, setTotalWeight] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedGrinding, setSelectedGrinding] = useState(null);
    const [selectedPackaging, setSelectedPackaging] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [wheatProduct, setWheatProduct] = useState(null);
    const [selectedWheatSize, setSelectedWheatSize] = useState(null);
    const [productQuantities, setProductQuantities] = useState({});
    const [addingToCart, setAddingToCart] = useState(false);
    const [pendingCartAdd, setPendingCartAdd] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [productsLoaded, setProductsLoaded] = useState(false);
    const [isGoingBack, setIsGoingBack] = useState(false);
    const [isAutoFillMode, setIsAutoFillMode] = useState(false);
    const [isCustomisePayment, setIsCustomisePayment] = useState(false);
    const [cartData, setCartData] = useState({});
    const autoFillAppliedRef = useRef(false);

    // ---------------------------------------------------
    // Edit Mode Detection
    // ---------------------------------------------------
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const isEdit = params.get("type") === "edit";
        setIsEditMode(isEdit);
        if (isEdit) {
            setIsAutoFillMode(true);
            autoFillAppliedRef.current = false;
            setStep(0);
        }
    }, [location.key]);

    // ---------------------------------------------------
    // Auto-fill from cart item (edit mode)
    // ---------------------------------------------------
    useEffect(() => {
        if (!isEditMode || !cartItemToUse) return;
        if (!isAutoFillMode || autoFillAppliedRef.current) return;

        applyAutoFill(cartItemToUse);
        autoFillAppliedRef.current = true;
        setIsAutoFillMode(false);
        setIsGoingBack(false);
    }, [isEditMode, cartItemToUse, isAutoFillMode]);

    const cleanWeight = (weightStr) => {
        if (!weightStr) return 1;
        weightStr = weightStr.replace(/\(.*?\)/g, '');
        weightStr = weightStr.replace(/kg/i, '');
        weightStr = weightStr.trim();
        const num = parseFloat(weightStr);
        return isNaN(num) ? 1 : num;
    };

    const applyAutoFill = (item) => {
        const attributes = item.attributes;
        let products = [];

        attributes.product?.forEach(p => {
            products.push({ id: p.id, name: p.name, price: p.price, categoryId: 1, unit: p.weight });
        });
        attributes.subProducts?.forEach(p => {
            products.push({ id: p.id, name: p.name, price: p.price, categoryId: 2, unit: p.weight });
        });
        setSelectedProducts(products);

        if (attributes.product?.length > 0) {
            setWheatProduct({
                id: attributes.product[0].id,
                name: attributes.product[0].name,
                price: attributes.product[0].price,
                categoryId: 1
            });
        }

        let quantities = {};
        [...(attributes.product || []), ...(attributes.subProducts || [])].forEach(p => {
            const weightNum = cleanWeight(p.weight);
            quantities[p.id] = {
                basePrice: p.price,
                totalPrice: p.totalPrice,
                weight: weightNum,
                selectedValue: `${weightNum} KG`,
                increasePerKg: p.increasePrice || 0,
                totalAdditional: (p.increasePrice || 0) * weightNum
            };
        });
        setProductQuantities(quantities);

        const grinding = attributes.attributes?.find(a => a.id === 5);
        const packaging = attributes.attributes?.find(a => a.id === 6);

        if (grinding) {
            setSelectedGrinding({ id: grinding.valueId, name: grinding.value, increasePrice: grinding.increasePrice });
        }
        if (packaging) {
            setSelectedPackaging({ id: packaging.valueId, name: packaging.value, increasePrice: packaging.increasePrice });
        }
    };

    // ---------------------------------------------------
    // Total Price & Weight Calculation
    // ---------------------------------------------------
    useEffect(() => {
        let newTotalPrice = 0;
        let newTotalWeight = 0;

        Object.values(productQuantities).forEach(qty => {
            newTotalPrice += parseFloat(qty.totalPrice || 0);
            newTotalWeight += parseFloat(qty.weight || 0);
        });

        const grindingPrice = parseFloat(selectedGrinding?.increasePrice || 0);
        const packagingPrice = parseFloat(selectedPackaging?.increasePrice || 0);
        const additionalPrice = (grindingPrice + packagingPrice) * newTotalWeight;

        setTotalPrice(newTotalPrice + additionalPrice);
        setTotalWeight(newTotalWeight);
    }, [productQuantities, selectedGrinding, selectedPackaging]);

    // ---------------------------------------------------
    // Handlers
    // ---------------------------------------------------
    const handleSelectionChange = useCallback((products) => {
        if (isAutoFillMode) return;
        if (isEditMode && (!products || products.length === 0)) return;

        setSelectedProducts(products || []);
        const wheat = products?.find(p => p.categoryId === 1);
        if (wheat) {
            localStorage.setItem('customise-wheatProductId', wheat.id.toString());
            setWheatProduct(wheat);
        } else {
            setWheatProduct(null);
        }
    }, [isAutoFillMode, isEditMode]);

    const handleWheatSizeChange = useCallback((sizeData) => {
        setSelectedWheatSize(sizeData);
    }, []);

    const handleProductQuantitiesChange = useCallback((quantities) => {
        setProductQuantities(quantities);
    }, []);

    const handleGrindingSelectionChange = (grinding, packaging) => {
        setSelectedGrinding(grinding);
        setSelectedPackaging(packaging);
        localStorage.setItem('customise-grindingSelection', JSON.stringify({ grinding, packaging }));
        if (grinding && packaging) {
            setValidationError('');
        }
    };

    // ---------------------------------------------------
    // Cart XML Helpers
    // ---------------------------------------------------
    const generateFullCatalogXml = useCallback(() => {
        const mainProductXml = localStorage.getItem('customise-mainProductXml') || '';
        const productsXml = localStorage.getItem('customise-productsXml') || '';
        const attributesXml = localStorage.getItem('customise-attributesXml') || '';
        return `<Catalog>${mainProductXml}${productsXml}${attributesXml}</Catalog>`;
    }, []);

    const clearCustomisationStorage = () => {
        [
            'customise-selectedProducts',
            'customise-wheatProductId',
            'customise-quantities',
            'customise-mainProductXml',
            'customise-productsXml',
            'customise-grindingSelection',
            'customise-attributesXml'
        ].forEach(key => localStorage.removeItem(key));
    };

    // ---------------------------------------------------
    // Add to Cart / Update Cart
    // ---------------------------------------------------
    const handleCartAction = useCallback(async () => {
        try {
            setAddingToCart(true);
            const freshUserId = localStorage.getItem('userId');

            if (!freshUserId) {
                setPendingCartAdd(true);
                const loginButton = document.querySelector('[data-bs-target="#loginModal"]');
                if (loginButton) loginButton.click();
                return false;
            }

            const userId = parseInt(freshUserId);
            const attributesXml = generateFullCatalogXml();
            let response;

            if (isEditMode && cartItemToUse) {
                const updateData = {
                    productId: wheatProduct?.id || 0,
                    shoppingCartTypeId: 2,
                    quantity: 1,
                    price: wheatProduct?.price || 0,
                    discountPrice: totalPrice,
                    totalPrice: totalPrice,
                    weight: totalWeight,
                    discountId: wheatProduct?.discountId || 0,
                    attributesXml,
                    userId
                };
                response = await UpdateCartItem(cartItemToUse.id, updateData);
            } else {
                const cartPayload = {
                    productId: wheatProduct?.id || 0,
                    shoppingCartTypeId: 2,
                    quantity: 1,
                    price: wheatProduct?.price || 0,
                    discountPrice: totalPrice,
                    totalPrice: totalPrice,
                    weight: totalWeight,
                    discountId: wheatProduct?.discountId || 0,
                    attributesXml,
                    userId
                };
                response = await AddToCart(cartPayload);
            }

            if (response.status) {
                window.dispatchEvent(new Event("cartUpdated"));
                clearCustomisationStorage();
                window.history.replaceState({}, "", "/customize-wheat");
                setStep(prev => prev + 1);
                return true;
            } else {
                setValidationError(response.message || 'Failed to add to cart');
                return false;
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            setValidationError('Failed to add to cart. Please try again.');
            return false;
        } finally {
            setAddingToCart(false);
        }
    }, [wheatProduct, totalPrice, totalWeight, generateFullCatalogXml, isEditMode, cartItemToUse]);

    // Retry cart add after login
    useEffect(() => {
        if (!pendingCartAdd) return;
        const userId = localStorage.getItem('userId');
        if (userId) {
            setPendingCartAdd(false);
            handleCartAction();
        }
    }, [pendingCartAdd, handleCartAction]);

    useEffect(() => {
        const handleStorageChange = () => {
            const userId = localStorage.getItem('userId');
            if (userId && pendingCartAdd) {
                setPendingCartAdd(false);
                handleCartAction();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [pendingCartAdd, handleCartAction]);

    // ---------------------------------------------------
    // Next / Prev Navigation
    // ---------------------------------------------------
    const handleNextClick = async () => {
        setValidationError('');

        // ---- STEP 0: Wheat is mandatory ----
        if (step === 0) {
            if (!wheatProduct) {
                setValidationError('Please select at least one wheat product to proceed.');
                return;
            }
            setIsAutoFillMode(false);
            localStorage.setItem('customise-selectedProducts', JSON.stringify(selectedProducts));
            setStep(step + 1);
            return;
        }

        // ---- STEP 1: Save quantity XML ----
        if (step === 1) {
            let mainProductXml = '';
            let productsXml = '<SubProducts>';
            const wheatProdId = wheatProduct?.id;

            selectedProducts.forEach(product => {
                const quantity = productQuantities[product.id];
                if (!quantity) return;
                const categoryId = product.categoryId || product.category?.id;

                if (categoryId === 1 && product.id === wheatProdId) {
                    mainProductXml = `<Product><Item Id='${product.id}' Name='${product.name}' Price='${quantity.basePrice || 0}' TotalPrice='${quantity.totalPrice || 0}' Weight='${quantity.selectedValue || '1KG'}' /></Product>`;
                } else {
                    if (quantity.selectedValue != null) {
                        productsXml += `<Item Id='${product.id}' Name='${product.name}' Price='${quantity.basePrice || 0}' TotalPrice='${quantity.totalPrice || 0}' Weight='${quantity.selectedValue || '1KG'}' />`;
                    }
                }
            });

            productsXml += '</SubProducts>';
            localStorage.setItem('customise-mainProductXml', mainProductXml);
            localStorage.setItem('customise-productsXml', productsXml);
            setStep(step + 1);
            return;
        }

        // ---- STEP 2: Grinding & Packaging (optional), then add to cart ----
        if (step === 2) {
            let attributesXml = '<Attributes>';
            if (selectedGrinding) {
                const grindingTotalIncrease = (parseFloat(selectedGrinding.increasePrice || 0) * totalWeight).toFixed(2);
                attributesXml += `<Item Id="5" Name="Grinding Texture" ValueId="${selectedGrinding.id}" Value="${selectedGrinding.name}" IncreasePrice="${selectedGrinding.increasePrice || 0}" TotalIncreasePrice="${grindingTotalIncrease}" />`;
            }
            if (selectedPackaging) {
                const packagingTotalIncrease = (parseFloat(selectedPackaging.increasePrice || 0) * totalWeight).toFixed(2);
                attributesXml += `<Item Id="6" Name="Packaging" ValueId="${selectedPackaging.id}" Value="${selectedPackaging.name}" IncreasePrice="${selectedPackaging.increasePrice || 0}" TotalIncreasePrice="${packagingTotalIncrease}" />`;
            }
            attributesXml += '</Attributes>';
            localStorage.setItem('customise-attributesXml', attributesXml);

            await handleCartAction();
            return;
        }

        // ---- STEP 3: Cart Detail validation ----
        if (step === 3) {
            if (!cartData?.cartItemIds || cartData.cartItemIds.length === 0 || !cartData?.amount || cartData.amount <= 0) {
                setValidationError('Your cart is empty. Please add grains or custom flour mix before proceeding to checkout.');
                return;
            }
        }

        // ---- STEP 4: Payment ----
        if (step === steps.length - 1) {
            setIsCustomisePayment(true);
        } else {
            setStep(step + 1);
        }
    };

    const handlePrev = () => {
        setIsGoingBack(true);
        // Restore previously saved selected products
        const saved = localStorage.getItem("customise-selectedProducts");
        if (saved) {
            setSelectedProducts(JSON.parse(saved));
        }
        setStep(prev => prev - 1);
    };

    // ---------------------------------------------------
    // Render
    // ---------------------------------------------------
    const cartAmount = cartData?.amount;

    return (
        <>
            {/* Sidebar Stepper */}
            <div className="col-xl-3">
                <div className="stepper-sidebar">
                    <div className="sidebar-step">
                        {steps.map((label, index) => (
                            <div
                                key={index}
                                className={`step-sidebar ${index < step ? 'completed' : ''} ${index === step ? 'active' : ''}`}
                            >
                                <div className="sidebar-content">
                                    <div className="step-count">{index + 1}</div>
                                    <span>{label}</span>
                                </div>
                                <div className="check-box-icon">
                                    <img className="img-fluid" src={CHeckGreenIcon} alt="check icon" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="col-xl-9">
                <div className="stepper">
                    <div className="stepper-content pt_42">
                        {getStepContent(
                            step,
                            handleSelectionChange,
                            selectedProducts,
                            handleGrindingSelectionChange,
                            handleWheatSizeChange,
                            selectedGrinding,
                            selectedPackaging,
                            productQuantities,
                            handleProductQuantitiesChange,
                            wheatProduct,
                            setProductsLoaded,
                            isGoingBack,
                            isCustomisePayment,
                            setIsCustomisePayment,
                            cartData,
                            setCartData
                        )}

                        {validationError && (
                            <div className="alert alert-danger mt-3" role="alert">
                                {validationError}
                            </div>
                        )}

                        <div className="stepper-button-content">
                            {/* Step 0 or Step 3 — only Next button, no Prev */}
                            {step === 0 || step === 3 ? (
                                <div className="d-flex align-items-center justify-content-end">
                                    <button
                                        onClick={handleNextClick}
                                        className="button btn-solid"
                                        disabled={addingToCart || (step === 3 && (!cartData?.cartItemIds || cartData.cartItemIds.length === 0))}
                                    >
                                        {addingToCart ? 'Adding...' : 'NEXT'}
                                    </button>
                                </div>
                            ) : step === 4 ? (
                                /* Step 4 — Prev + Pay button */
                                <div className="d-flex align-items-center">
                                    <button
                                        onClick={handlePrev}
                                        className="button btn-border mr_10"
                                    >
                                        Prev
                                    </button>
                                    <button
                                        onClick={handleNextClick}
                                        className="button btn-solid"
                                        disabled={addingToCart}
                                    >
                                        {cartAmount != null
                                            ? `Pay ${cartAmount.toFixed(2)} ₹`
                                            : 'Pay Now'
                                        }
                                    </button>
                                </div>
                            ) : (
                                /* Steps 1 & 2 — Total + Prev + Next */
                                <>
                                    <div className="d-flex align-items-center total-text">
                                        <p className="fs_20 fw_500 mb-0 mr_32">
                                            Total QTY: {totalWeight.toFixed(2)} KG
                                        </p>
                                        <p className="fs_20 fw_500 mb-0 mr_32 d-flex align-items-center mr_28">
                                            Sub Total: {totalPrice.toFixed(2)} ₹
                                            <span className="d-flex ml_14">
                                                <img className="img-fluid" src={InfoIcon} alt="Info Icon" />
                                            </span>
                                        </p>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <button
                                            onClick={handlePrev}
                                            className="button btn-border mr_10"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            onClick={handleNextClick}
                                            className="button btn-solid"
                                            disabled={addingToCart}
                                        >
                                            {addingToCart ? 'Adding to Cart...' : 'NEXT'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomiseSidebar;